import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api";
import type { SessionUser } from "@shared/types";

interface AuthCtx {
  user: SessionUser | null;
  loading: boolean;
  login: (username: string, password: string, totp?: string) => Promise<{ mfaRequired?: boolean }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  setUser: (u: SessionUser | null) => void;
  can: (perm: string) => boolean;
  hasRole: (key: string) => boolean;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { user } = await api.get<{ user: SessionUser | null }>("/auth/me");
      setUser(user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (username: string, password: string, totp?: string) => {
    const res = await api.post<{ user?: SessionUser; mfaRequired?: boolean }>("/auth/login", { username, password, totp });
    if (res.mfaRequired) return { mfaRequired: true };
    setUser(res.user ?? null);
    return {};
  }, []);

  const logout = useCallback(async () => {
    await api.post("/auth/logout");
    setUser(null);
  }, []);

  const can = useCallback(
    (perm: string) => !!user && (user.isSuperAdmin || user.permissions.includes(perm)),
    [user],
  );
  const hasRole = useCallback((key: string) => !!user?.roles.some((r) => r.key === key), [user]);

  return (
    <Ctx.Provider value={{ user, loading, login, logout, refresh, setUser, can, hasRole }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
